export interface OwnedRuntimeResource<T> {
  readonly value: T;
  /** Disposes this resource now. Later scope disposal remains harmless. */
  dispose(): void;
}

type Cleanup = () => void;

/**
 * Owns the external resources created by one UI/runtime lifetime. Cleanup is
 * idempotent, LIFO, and comprehensive even when an individual disposer fails.
 */
export class RuntimeScope {
  private readonly cleanups = new Set<Cleanup>();
  private readonly timeouts = new Map<number, Cleanup>();
  private readonly abortController = new AbortController();
  private closed = false;
  private cleanupErrors: unknown[] = [];

  get signal(): AbortSignal {
    return this.abortController.signal;
  }

  get disposed(): boolean {
    return this.closed;
  }

  /** Number of resources still awaiting cleanup, useful for runtime diagnostics. */
  get pendingCleanupCount(): number {
    return this.cleanups.size;
  }

  /** Registers a cleanup and returns an idempotent function that runs it now. */
  defer(cleanup: Cleanup): Cleanup {
    let pending: Cleanup | null = cleanup;
    const wrapped = (): void => {
      if (!pending) return;
      const run = pending;
      pending = null;
      this.cleanups.delete(wrapped);
      try {
        run();
      } catch (error) {
        this.cleanupErrors.push(error);
      }
    };
    if (this.closed) wrapped();
    else this.cleanups.add(wrapped);
    return wrapped;
  }

  own<T>(value: T, dispose: (value: T) => void): OwnedRuntimeResource<T> {
    const cleanup = this.defer(() => dispose(value));
    return { value, dispose: cleanup };
  }

  listen<TEvent extends Event = Event>(
    target: EventTarget,
    type: string,
    listener: ((event: TEvent) => void) | EventListenerObject,
    options?: boolean | AddEventListenerOptions,
  ): Cleanup {
    if (this.closed) return () => undefined;
    const eventListener = listener as EventListenerOrEventListenerObject;
    target.addEventListener(type, eventListener, options);
    return this.defer(() => target.removeEventListener(type, eventListener, options));
  }

  timeout(callback: () => void, delayMs: number): number | undefined {
    if (this.closed) return undefined;
    let cancel = (): void => undefined;
    const timer = globalThis.setTimeout(() => {
      cancel();
      if (!this.closed) callback();
    }, Math.max(0, delayMs));
    const id = timer as unknown as number;
    cancel = this.defer(() => {
      this.timeouts.delete(id);
      globalThis.clearTimeout(timer);
    });
    this.timeouts.set(id, cancel);
    return id;
  }

  cancelTimeout(timer: number): void {
    this.timeouts.get(timer)?.();
  }

  interval(callback: () => void, delayMs: number): number | undefined {
    if (this.closed) return undefined;
    const timer = globalThis.setInterval(() => {
      if (!this.closed) callback();
    }, Math.max(0, delayMs));
    this.defer(() => globalThis.clearInterval(timer));
    return timer as unknown as number;
  }

  animationFrame(callback: FrameRequestCallback): number | undefined {
    if (this.closed || typeof globalThis.requestAnimationFrame !== 'function') return undefined;
    let cancel = (): void => undefined;
    const frame = globalThis.requestAnimationFrame((time) => {
      cancel();
      if (!this.closed) callback(time);
    });
    cancel = this.defer(() => globalThis.cancelAnimationFrame(frame));
    return frame;
  }

  child(): RuntimeScope {
    const child = new RuntimeScope();
    const release = this.defer(() => {
      child.dispose();
    });
    child.defer(release);
    return child;
  }

  /** Returns every cleanup failure after attempting all registered cleanup. */
  dispose(): readonly unknown[] {
    if (this.closed) return this.cleanupErrors;
    this.closed = true;
    this.abortController.abort();
    // Snapshot the reverse order: callbacks can dispose other resources.
    for (const cleanup of [...this.cleanups].reverse()) cleanup();
    this.cleanups.clear();
    return this.cleanupErrors;
  }
}
